import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useToast, useOpenList, useLists } from '../../context';
import { useLang, useAutoTranslate, SOURCE_LANG } from '../../i18n';
import { useClosing } from '../../hooks/useClosing';
import { useListNames } from '../../hooks/useListNames';
import { useSelection } from '../../hooks/useSelection';
import { useStableGroups } from '../../hooks/useStable';
import { normalizeName } from '../../logic/names';
import { suggestCategory } from '../../logic/categorize';
import { EmptyState, Loading } from '../shared/EmptyState';
import { ClosingSortSheet } from './ClosingSortSheet';
import { ClosingItemRow } from './ClosingItemRow';
import { InlineName, TargetSelect, EditTools, SelectBox, AddRow } from './parts';
import { useClosingActions } from './useClosingActions';

/**
 * ClosingTab — the end-of-day walk-through. A curated tree the closing crew walks:
 *   Section → Sub-section → Items.
 * Checking a low item adds it to the Today board under its list (toggle: unchecking
 * removes it). `target` inherits down the tree (item → sub-section → section), so a
 * sub-section can steer a whole group to a list. Both levels expand/collapse. Edit mode
 * gives add / rename / delete / reorder + set-list at all three levels, plus multi-select:
 * item boxes (or 🗑) pick items, tri-state section / sub-section boxes pick every item
 * beneath them → "↪ Move to…" another sub-section or "Delete N", each one batch with undo
 * (containers are kept).
 * "✨ Sort into lists" (edit mode) reviews each item's list against a suggestion from its
 * name (logic/categorize.js). Every list change — that sheet or any level's dropdown — is
 * one batch that also re-files today's board rows, with undo (useClosingActions).
 * Picking an item's list by hand sets `keepList`, so suggestions stop nagging about it. The
 * checkbox reflects the LIVE board, so it resets each day; seeded once from the sheets.
 */

const NO_NAMES = [];
const NO_ROWS = [];
const byOrder = (a, b) => (a.order || 0) - (b.order || 0);
const bySub = item => item.subsectionId;

export function ClosingTab({ todayItems, author }) {
  const { t, ln, lang } = useLang();
  const notify = useToast();
  const lists = useLists();
  const { catById, route } = lists;
  const { short } = useListNames();
  const { withList, board } = useOpenList();
  // A new list cycle has begun (closing time) and nobody has started tonight's list yet:
  // the walk shows a FRESH sheet (last night's ticks belong to the old list), and every
  // tap goes through the "new list or same list?" question — never deletes old entries.
  const newCycle = !!(board && board.stale);
  const { sections, subsections, items, loading } = useClosing();
  const [editing, setEditing] = useState(false);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [sorting, setSorting] = useState(false);               // "Sort into lists" sheet open
  const { selected, toggle: toggleSelect, toggleAll, clear: clearSelection, keepOnly } = useSelection();

  useAutoTranslate(useMemo(() => (lang === SOURCE_LANG ? NO_NAMES
    : [...new Set([...sections.map(s => s.title), ...subsections.map(s => s.title), ...items.map(i => i.name)])]),
  [lang, sections, subsections, items]));

  // The tree in walk order. Item groups keep their identity while unchanged (memoized rows).
  const orderedSections = useMemo(() => [...sections].sort(byOrder), [sections]);
  const subsBySection = useMemo(() => {
    const m = new Map();
    subsections.forEach(s => { if (!m.has(s.sectionId)) m.set(s.sectionId, []); m.get(s.sectionId).push(s); });
    m.forEach(a => a.sort(byOrder));
    return m;
  }, [subsections]);
  const sortedItems = useMemo(() => [...items].sort(byOrder), [items]);
  const itemsBySub = useStableGroups(sortedItems, bySub);
  const secById = useMemo(() => new Map(sections.map(s => [s.id, s])), [sections]);
  const subById = useMemo(() => new Map(subsections.map(s => [s.id, s])), [subsections]);
  const itemById = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  // Today's board by "list|name". An item's board row lives in the list its name routes
  // to (e.g. a dal ticked in a Groceries sub-section lands on Chef Groceries).
  const boardIndex = useMemo(() => {
    const m = new Map();
    todayItems.forEach(i => m.set(`${i.category}|${normalizeName(i.name)}`, i));
    return m;
  }, [todayItems]);
  const onBoard = useCallback((name, target) => boardIndex.get(`${route(name, target)}|${normalizeName(name)}`), [boardIndex, route]);
  const ticked = (name, target) => !newCycle && !!onBoard(name, target);   // what the boxes show

  const actions = useClosingActions({
    sections, subsections, items, orderedSections, subsBySection, itemsBySub, secById, subById, itemById,
    onBoard, newCycle, lists, route, author, notify, t, ln, short, withList, selected, clearSelection,
  });

  const toggleCollapse = id => setCollapsed(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // Leaving edit mode drops the selection; ids deleted elsewhere drop out of it too.
  useEffect(() => { if (!editing) clearSelection(); }, [editing, clearSelection]);
  useEffect(() => { keepOnly([...itemById.keys()]); }, [itemById, keepOnly]);

  // ✨ Sort into lists: every item in walk order with its list, a name-based suggestion,
  // and whether its list was picked by hand (`kept` — never flagged as a mismatch).
  const sortRows = useMemo(() => orderedSections.flatMap(sec => (subsBySection.get(sec.id) || []).flatMap(su => {
    const subTarget = su.target || sec.target;
    return (itemsBySub.get(su.id) || []).map(it => ({
      id: it.id, name: it.name, path: `${ln(sec.title)} › ${ln(su.title)}`,
      current: it.target || subTarget, suggestion: suggestCategory(it.name, it), kept: !!it.keepList,
    }));
  })), [orderedSections, subsBySection, itemsBySub, ln]);
  const mismatches = useMemo(
    () => sortRows.filter(r => !r.kept && r.suggestion && r.suggestion.id !== r.current).length, [sortRows]);

  if (loading) return <Loading label={t('common.loading')} />;

  // [done, total] for a sub-section, each item resolved through the tree.
  const subStats = (sub, section) => {
    const list = itemsBySub.get(sub.id) || NO_ROWS;
    return [list.filter(it => ticked(it.name, it.target || sub.target || section.target)).length, list.length];
  };

  return (
    <div className="do-cl">
      <div className="do-cl__bar">
        <p className="do-cl__intro">{t('closing.intro')}</p>
        <span className="do-checklist__spacer" />
        {editing && items.length > 0 && (
          <button className={`do-cl__sortbtn${mismatches ? ' do-cl__sortbtn--hot' : ''}`} onClick={() => setSorting(true)} title={t('sort.buttonHint')}>
            {t('sort.button')}{mismatches > 0 && <span className="do-cl__sortbtn-n">{mismatches}</span>}
          </button>
        )}
        <button className={`do-focus${editing ? ' do-focus--on' : ''}`} onClick={() => setEditing(e => !e)}>
          {editing ? t('closing.doneEditing') : t('closing.edit')}
        </button>
      </div>

      {newCycle && !editing && orderedSections.length > 0 && <p className="do-cl__cyclehint">🌙 {t('closing.newCycleHint')}</p>}
      {orderedSections.length > 0 && subsections.length === 0 && (
        <div className="do-cl__loadhint">
          <span className="do-cl__loadhint-txt">📋 {t('closing.emptyHint')}</span>
          <button className="do-cl__add-sec" onClick={actions.loadDefaults}>{t('closing.loadDefault')}</button>
        </div>
      )}

      {orderedSections.map((sec, si) => {
        const subs = subsBySection.get(sec.id) || NO_ROWS;
        const secItems = subs.flatMap(su => itemsBySub.get(su.id) || NO_ROWS);
        const secCollapsed = collapsed.has(sec.id);
        let sDone = 0; let sTotal = 0;
        subs.forEach(su => { const [d, n] = subStats(su, sec); sDone += d; sTotal += n; });
        return (
          <section key={sec.id} className="do-cl__sec" style={{ '--cc': catById(sec.target).color }}>
            <div className="do-cl__sec-head">
              <button className="do-cl__sec-toggle" onClick={() => toggleCollapse(sec.id)} aria-expanded={!secCollapsed} aria-label={ln(sec.title)}>
                <span className="do-cl__chev">{secCollapsed ? '▸' : '▾'}</span>
                <span className="do-cl__sec-icon">{sec.icon || '📋'}</span>
              </button>
              {editing && <SelectBox list={secItems} selected={selected} onToggle={() => toggleAll(secItems.map(i => i.id))} label={t('closing.selectAllIn', { title: ln(sec.title) })} />}
              {editing
                ? <InlineName className="do-cl__sec-name-edit" value={sec.title} ariaLabel={t('closing.sectionName')} onSave={v => actions.renameSection(sec, v)} />
                : <button className="do-cl__sec-name" onClick={() => toggleCollapse(sec.id)}>{ln(sec.title)}</button>}
              <span className="do-cl__sec-count">{sDone}/{sTotal}</span>
              {editing && (
                <div className="do-cl__sec-tools">
                  <TargetSelect value={sec.target} ariaLabel={t('closing.sectionTarget')} onChange={v => actions.setSectionTarget(sec, v)} />
                  <EditTools onUp={() => actions.moveSection(si, -1)} onDown={() => actions.moveSection(si, 1)} canUp={si > 0}
                    canDown={si < orderedSections.length - 1} onDelete={() => actions.deleteSection(sec)} deleteLabel={t('closing.deleteSection')} />
                </div>
              )}
            </div>

            {!secCollapsed && (
              <div className="do-cl__subs">
                {subs.map((sub, sj) => {
                  const subTarget = sub.target || sec.target;
                  const list = itemsBySub.get(sub.id) || NO_ROWS;
                  const [subDone, subTotal] = subStats(sub, sec);
                  const subCollapsed = collapsed.has(sub.id);
                  return (
                    <div key={sub.id} className="do-cl__sub" style={{ '--cc': catById(subTarget).color }}>
                      <div className="do-cl__sub-head">
                        <button className="do-cl__sub-toggle" onClick={() => toggleCollapse(sub.id)} aria-expanded={!subCollapsed} aria-label={ln(sub.title)}>
                          <span className="do-cl__chev">{subCollapsed ? '▸' : '▾'}</span>
                        </button>
                        {editing && <SelectBox list={list} selected={selected} onToggle={() => toggleAll(list.map(i => i.id))} label={t('closing.selectAllIn', { title: ln(sub.title) })} />}
                        {editing
                          ? <InlineName className="do-cl__sub-name-edit" value={sub.title} ariaLabel={t('closing.subsectionName')} onSave={v => actions.renameSub(sub, v)} />
                          : <button className="do-cl__sub-name" onClick={() => toggleCollapse(sub.id)}>{ln(sub.title)}</button>}
                        <span className="do-cl__sub-count">{subDone}/{subTotal}</span>
                        {editing && (
                          <div className="do-cl__item-tools">
                            <TargetSelect value={subTarget} ariaLabel={t('closing.subsectionTarget')} onChange={v => actions.setSubTarget(sub, v)} />
                            <EditTools onUp={() => actions.moveSub(subs, sj, -1)} onDown={() => actions.moveSub(subs, sj, 1)} canUp={sj > 0}
                              canDown={sj < subs.length - 1} onDelete={() => actions.deleteSub(sub)} deleteLabel={t('closing.deleteSubsection')} />
                          </div>
                        )}
                      </div>

                      {!subCollapsed && (
                        <div className="do-cl__items">
                          {list.map((it, ii) => (
                            <ClosingItemRow key={it.id} item={it} sub={sub} section={sec} subTarget={subTarget}
                              checked={ticked(it.name, it.target || subTarget)} selected={selected.has(it.id)} editing={editing}
                              index={ii} siblings={list} actions={actions} onSelect={toggleSelect} />
                          ))}
                          {list.length === 0 && !editing && <p className="do-cl__empty">{t('closing.sectionEmpty')}</p>}
                          {editing && <AddRow placeholder={t('closing.addItem')} onAdd={name => actions.addItemTo(sub, subTarget, name)} />}
                        </div>
                      )}
                    </div>
                  );
                })}
                {subs.length === 0 && !editing && <p className="do-cl__empty">{t('closing.sectionEmpty')}</p>}
                {editing && <button className="do-cl__add-sub" onClick={() => actions.addSub(sec)}>{t('closing.addSubsection')}</button>}
              </div>
            )}
          </section>
        );
      })}

      {editing && orderedSections.length > 0 && (
        <div className="do-cl__edit-actions">
          <button className="do-cl__add-sec" onClick={actions.addSection}>{t('closing.addSection')}</button>
          <button className="do-cl__reset" onClick={actions.resetToDefaults}>{t('closing.reset')}</button>
        </div>
      )}
      {editing && selected.size > 0 && (
        <div className="do-cl__bulkbar" role="region" aria-label={t('closing.selectedN', { n: selected.size })}>
          <span className="do-cl__bulkbar-txt">{t('closing.selectedN', { n: selected.size })}</span>
          <button className="do-cl__bulkbar-cancel" onClick={clearSelection}>{t('closing.clearSelection')}</button>
          {/* Picking a sub-section moves the selection there at once (undo in the toast). */}
          <select className="do-cl__bulkbar-move" value="" aria-label={t('closing.moveTo')}
            onChange={e => { if (e.target.value) actions.moveSelected(e.target.value); }}>
            <option value="">{t('closing.moveTo')}</option>
            {orderedSections.map(sec => (
              <optgroup key={sec.id} label={`${sec.icon || '📋'} ${ln(sec.title)}`}>
                {(subsBySection.get(sec.id) || NO_ROWS).map(su => <option key={su.id} value={su.id}>{ln(su.title)}</option>)}
              </optgroup>
            ))}
          </select>
          <button className="do-cl__bulkbar-del" onClick={actions.deleteSelected}>🗑 {t('closing.deleteSelected', { n: selected.size })}</button>
        </div>
      )}
      {sorting && (
        <ClosingSortSheet rows={sortRows} onApply={plan => { setSorting(false); actions.applySort(plan); }} onClose={() => setSorting(false)}
          t={t} ln={ln} lists={lists.active} catById={catById} short={short} />
      )}
      {orderedSections.length === 0 && (
        <EmptyState icon="📋">
          <p>{t('closing.empty')}</p>
          <button className="do-cl__add-sec" onClick={actions.loadDefaults}>{t('closing.loadDefault')}</button>
        </EmptyState>
      )}
    </div>
  );
}
